import { tool } from "ai";
import { z } from "zod";
import { createGlobalColor, getGlobalColors } from "../globalTools";
import { editOrCreateDynamicGrid, findDynamicData } from "../metaTools";
import { validateAndMergeJSX } from "@builder/store/slices/BuilderSlice/mergeJSXValidation";


export const generateDesignAIToolset = ({
    layerId,
    startingNodeId
}: {
    layerId: string;
    startingNodeId?: string;
}) => {
    return {
        getColorScheme: tool({
            description: "Returns the color scheme used on the page. Use this tool when you need to change the colors of the page.",
            parameters: z.object({}),
            execute: async () => {
                return "Use can use the vars in the css. " + JSON.stringify(getGlobalColors());
            }
        }),
        addColorToColorScheme: tool({
            description: "Adds a color to the color scheme. Use this tool when you need to add a color to the color scheme, if it's not already there.",
            parameters: z.object({
                color: z.string().describe("The color to add to the color scheme."),
                name: z.string().describe("The name of the color to add to the color scheme.")
            }),
            execute: async ({ color, name }) => {
                return createGlobalColor(color, name);
            }
        }),
    
        injectJSX: tool({
            description: "Injects a JSX string into the page. Use this tool to make changes to the page. Any change you make should be done with this tool. If you don't send the elementToReplace, it will override the whole page. If you want to add something, you should override the parent element. ",
            parameters: z.object({
                jsx: z.string().describe("The JSX string to inject into the page. MUST BE VALID JSX. NO MARKDOWN, NO CODE FENCES, NO ESCAPED CHARACTERS, NO THINGS THAT ARE NOT STRING JSX. NO NEWLINES. JUST <JSX>...</>. for example: <div>Hello</div>"),
                elementToReplace: z.string().describe("The id of the element to replace with the new JSX. If omitted, it will override the whole page!")
            }),
            execute: async ({ jsx, elementToReplace }) => {
                console.group("injecting jsx into page, elementToReplace:", elementToReplace);
                console.log(jsx);
                console.groupEnd();

                try {
                    validateAndMergeJSX({
                        jsx,
                        targetLayerId: layerId,
                        targetElement: elementToReplace || startingNodeId,
                    });
                    return "JSX merged successfully";
                } catch (e) {
                    console.error("Failed to merge JSX", e);
                    return `Failed to merge JSX: ${(e as Error).message}`;
                }
            }
        }),
        searchDynamicData: tool({
            description: "Find dynamic data fields by description or browse all available data. Use this when the user asks about what data is available, needs a specific data field, or wants to use dynamic content like customer info, order details, etc.",
            parameters: z.object({
                query: z.string().optional().describe("(Optional) Natural language description of the data you're looking for, e.g. 'customer name', 'order total', 'product price'. If omitted, returns all available data."),
                context: z.enum(["product", "cart", "order", "customer", "general"]).optional().describe("(Optional) Specific context where this data will be used"),
                numberOfResults: z.number().int().min(1).max(20).optional().describe("(Optional) Number of results to return. Defaults to 5 for search, 50 for browse all.")
            }),
            execute: async ({ query, context, numberOfResults }) => {
                return findDynamicData(query, context as any, numberOfResults);
            }
            }),
        
        editOrCreateDynamicGrid: tool({
            description: "You MUST use this tool for ANY editing or creation of ProductList, ProductGrid, CustomerList, OrderList. It will give you the JSX you need. Then you can edit the external styles of the grid to make it fit the page, but don't make any other changes yourself",
            parameters: z.object({
                gridType: z.enum(["product", "order", "customer"]).describe("Type of dynamic grid/list."),
                instructions: z.string().describe("Description of modifications to apply / new grid/list to create. Elaborate"),
                existingGridId: z.string().optional().describe("Existing ID of a grid/list to edit. Omit to create new.")
            }),
            execute: async ({ gridType, instructions, existingGridId }) => {
                return editOrCreateDynamicGrid({ instructions, gridType, existingGridId, layerId });
            }
        }),
    }
}

